import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

const navItems = [
  { label: "Home", href: "/" },
  {
    label: "Solutions",
    href: "/solutions",
    children: [
      { label: "Overview", href: "/solutions" },
      { label: "Defence & Counter-UAS", href: "/solutions/defence-cuas", description: "Analysis, mission integration & autonomy" },
      { label: "Protective Security", href: "/solutions/protective-security", description: "Helais Sentinel for site protection" },
      { label: "Public Safety", href: "/solutions/public-safety", description: "Emergency response & mission orchestration" },
      { label: "Civil Intelligence", href: "/solutions/civil-intelligence", description: "Operational analytics from existing infrastructure" },
    ],
  },
  {
    label: "Platforms & Technology",
    href: "/platforms",
    children: [
      { label: "Overview", href: "/platforms" },
      { label: "UAVSimNet", href: "https://c-uas.co.uk", description: "Simulation & decision support", external: true },
      { label: "AI-AV", href: "/platforms/ai-av", description: "Vision-based autonomous guidance" },
      { label: "Helais", href: "/platforms/helais", description: "Mission orchestration platform" },
      { label: "UrbanAnalytix", href: "https://urban-analytix.co.uk", description: "Operational intelligence", external: true },
      { label: "Advanced Development", href: "/platforms/advanced-development", description: "Design assets & development programmes" },
    ],
  },
  {
    label: "Company",
    href: "/company",
    children: [
      { label: "About RLK", href: "/company" },
      { label: "Our Companies", href: "/company/structure", description: "Group structure & divisions" },
      { label: "Intellectual Property", href: "/company/intellectual-property", description: "Patents, trademarks & designs" },
    ],
  },
  { label: "Evidence & Assurance", href: "/evidence" },
  { label: "Insights", href: "/insights" },
  { label: "Contact", href: "/contact" },
];

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
          scrolled
            ? "bg-background/90 backdrop-blur-xl border-b border-border/50"
            : "bg-transparent"
        )}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link to="/" className="relative z-10 flex-shrink-0">
              <img
                src="https://customer-assets.emergentagent.com/job_rlk-modern/artifacts/wepugaac_RLK%20Grey%20group.png"
                alt="The RLK Group"
                className="h-9 lg:h-10 w-auto"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => item.children && setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {item.children ? (
                    <>
                      <button
                        className={cn(
                          "flex items-center gap-1 px-4 py-2 text-sm font-medium rounded transition-colors",
                          isActive(item.href)
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {item.label}
                        <ChevronDown className={cn(
                          "h-3.5 w-3.5 transition-transform duration-200",
                          activeDropdown === item.label && "rotate-180"
                        )} />
                      </button>
                      
                      <div
                        className={cn(
                          "absolute top-full left-0 pt-2 transition-all duration-200",
                          activeDropdown === item.label
                            ? "opacity-100 visible translate-y-0"
                            : "opacity-0 invisible -translate-y-2"
                        )}
                      >
                        <div className="w-72 rounded border border-border bg-popover/95 backdrop-blur-xl shadow-lg p-2">
                          {item.children.map((child, idx) => (
                            child.external ? (
                              <a
                                key={child.href}
                                href={child.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block rounded px-3 py-2.5 transition-colors hover:bg-muted/50"
                              >
                                <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                                  {child.label}
                                  <ExternalLink className="h-3 w-3 text-muted-foreground" />
                                </span>
                                {child.description && (
                                  <span className="block text-xs text-muted-foreground mt-0.5">
                                    {child.description}
                                  </span>
                                )}
                              </a>
                            ) : (
                              <Link
                                key={child.href}
                                to={child.href}
                                className={cn(
                                  "block rounded px-3 py-2.5 transition-colors",
                                  isActive(child.href) && child.href !== item.href
                                    ? "bg-muted text-foreground"
                                    : "hover:bg-muted/50"
                                )}
                              >
                                <span className={cn(
                                  "block text-sm font-medium",
                                  idx === 0 ? "text-muted-foreground" : "text-foreground"
                                )}>
                                  {child.label}
                                </span>
                                {child.description && (
                                  <span className="block text-xs text-muted-foreground mt-0.5">
                                    {child.description}
                                  </span>
                                )}
                              </Link>
                            )
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={cn(
                        "px-4 py-2 text-sm font-medium rounded transition-colors",
                        isActive(item.href)
                          ? "text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            <div className="hidden lg:block">
              <Link
                to="/contact"
                className="btn-secondary h-9 px-5 text-sm"
              >
                Request a Briefing
              </Link>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden relative z-10 p-2 -mr-2 text-foreground"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <div
        className={cn(
          "fixed inset-0 z-40 lg:hidden transition-all duration-500",
          mobileOpen
            ? "opacity-100 visible"
            : "opacity-0 invisible pointer-events-none"
        )}
      >
        <div className="absolute inset-0 bg-background/98 backdrop-blur-xl" />
        
        <div className="relative h-full flex flex-col pt-20 pb-8 px-6 overflow-y-auto">
          <nav className="flex-1">
            <ul className="space-y-1">
              {navItems.map((item, index) => (
                <li
                  key={item.label}
                  className={cn(
                    "opacity-0",
                    mobileOpen && "animate-fade-up"
                  )}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {item.children ? (
                    <div className="py-2">
                      <Link
                        to={item.href}
                        className={cn(
                          "block text-2xl font-heading font-semibold tracking-tight transition-colors",
                          isActive(item.href) ? "text-foreground" : "text-muted-foreground"
                        )}
                      >
                        {item.label}
                      </Link>
                      <div className="mt-3 ml-4 space-y-2 border-l border-border/50 pl-4">
                        {item.children.slice(1).map((child) => (
                          child.external ? (
                            <a
                              key={child.href}
                              href={child.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block py-1 transition-colors text-muted-foreground hover:text-foreground"
                            >
                              <span className="flex items-center gap-1.5 text-base font-medium">
                                {child.label}
                                <ExternalLink className="h-3.5 w-3.5" />
                              </span>
                              {child.description && (
                                <span className="block text-sm text-muted-foreground/70 mt-0.5">
                                  {child.description}
                                </span>
                              )}
                            </a>
                          ) : (
                            <Link
                              key={child.href}
                              to={child.href}
                              className={cn(
                                "block py-1 transition-colors",
                                isActive(child.href)
                                  ? "text-foreground"
                                  : "text-muted-foreground hover:text-foreground"
                              )}
                            >
                              <span className="text-base font-medium">{child.label}</span>
                              {child.description && (
                                <span className="block text-sm text-muted-foreground/70 mt-0.5">
                                  {child.description}
                                </span>
                              )}
                            </Link>
                          )
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={cn(
                        "block py-2 text-2xl font-heading font-semibold tracking-tight transition-colors",
                        isActive(item.href) ? "text-foreground" : "text-muted-foreground"
                      )}
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div
            className={cn(
              "mt-8 pt-8 border-t border-border/50 opacity-0",
              mobileOpen && "animate-fade-up delay-300"
            )}
          >
            <Link to="/contact" className="btn-primary w-full justify-center">
              Request a Briefing
            </Link>
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">info@rlktech.co.uk</p>
              <p className="text-sm text-muted-foreground">0141 319 4813</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
